import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, PencilSimple, UploadSimple, Trash, X, CurrencyDollar,
  Truck, ArrowsClockwise, Check, PencilLine, Certificate, Selection,
} from "@phosphor-icons/react";
import { api, formatErr } from "@/lib/api";
import { saveBrowseContext } from "@/lib/browseContext";
import ListingCard from "@/components/ListingCard";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_OPTIONS = ["active", "pending", "hold", "sold", "removed"];
const CONDITION_OPTIONS = ["Mint", "Near Mint", "Excellent", "Very Good", "Good", "Fair", "Poor", "Ungraded"];
const GRADING_COMPANIES = ["PSA", "BGS", "SGC", "CGC", "HGA", "Other"];

// Tab config for the status filter row.  `null` = All.
const STATUS_TABS = [
  { value: null,      label: "All",     accent: "" },
  { value: "active",  label: "Active",  accent: "h57-chip-orange" },
  { value: "pending", label: "Pending", accent: "" },
  { value: "hold",    label: "On Hold", accent: "" },
  { value: "sold",    label: "Sold",    accent: "h57-chip-gold" },
];

// Kind → { label, icon }.  Each opens its own dialog form.
const BULK_ACTIONS = [
  { kind: "price", label: "Price", icon: CurrencyDollar },
  { kind: "shipping", label: "Shipping", icon: Truck },
  { kind: "offers", label: "Offers", icon: ArrowsClockwise },
  { kind: "status", label: "Status", icon: Check },
  { kind: "condition", label: "Condition", icon: Selection },
  { kind: "grading", label: "Grading", icon: Certificate },
  { kind: "notes", label: "Notes", icon: PencilLine },
];

export default function MyListings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [statusTab, setStatusTab] = useState("active"); // "active" | "pending" | "hold" | "sold" | null (=all)
  // Selection state — cleared whenever the active tab changes so bulk edits
  // never span across tabs unexpectedly.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkKind, setBulkKind] = useState(null);          // active dialog kind
  const [bulkForm, setBulkForm] = useState({});             // dialog form state
  const [bulkRunning, setBulkRunning] = useState(false);
  // Shadcn AlertDialog for bulk-delete confirmation (replaces window.confirm).
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null); // single-listing delete

  const load = async () => {
    try {
      const { data } = await api.get("/listings/mine");
      setListings((data.listings || []).filter((l) => l.status !== "removed"));
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Persist visible order for prev/next navigation on ListingDetail.
  useEffect(() => {
    const visible = statusTab ? listings.filter((l) => l.status === statusTab) : listings;
    if (visible.length) {
      saveBrowseContext(visible.map((l) => l.id), `mine:${statusTab || "all"}`);
    }
  }, [listings, statusTab]);

  // Filter listings by the active status tab.
  const visibleListings = statusTab
    ? listings.filter((l) => l.status === statusTab)
    : listings;

  // Per-status counts for the tab badges — precompute once.
  const statusCounts = listings.reduce((acc, l) => {
    acc[l.status] = (acc[l.status] || 0) + 1;
    return acc;
  }, {});
  statusCounts.all = listings.length;

  const changeTab = (value) => {
    if (value === statusTab) return;
    setStatusTab(value);
    // Clear selection when switching tabs so a bulk action can't accidentally
    // hit hidden listings.
    setSelectedIds(new Set());
  };

  const allSelected = visibleListings.length > 0 && selectedIds.size === visibleListings.length;
  const someSelected = selectedIds.size > 0;

  const toggleOne = (id) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(visibleListings.map((l) => l.id)));
  const clearAll = () => setSelectedIds(new Set());

  const deleteListing = async (l) => {
    if (!window.confirm(`Delete this listing?\n\n"${l.title}"\n\nThis will remove it from the marketplace immediately.`)) return;
    setDeletingId(l.id);
    try {
      await api.delete(`/listings/${l.id}`);
      setListings((prev) => prev.filter((x) => x.id !== l.id));
      setSelectedIds((s) => { const n = new Set(s); n.delete(l.id); return n; });
      toast.success("Listing deleted");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setDeletingId(null); }
  };

  // ---- Bulk actions ----
  const openBulk = (kind) => {
    setBulkKind(kind);
    // Sensible default form per action
    if (kind === "price" || kind === "shipping") setBulkForm({ op: "set", value: "" });
    else if (kind === "offers") setBulkForm({ accepts_offers: true });
    else if (kind === "status") setBulkForm({ status: "active" });
    else if (kind === "condition") setBulkForm({ condition: "Near Mint" });
    else if (kind === "grading") setBulkForm({ graded: true, grading_company: "PSA", grade: "10" });
    else if (kind === "notes") setBulkForm({ notes: "" });
  };
  const closeBulk = () => { setBulkKind(null); setBulkForm({}); };

  // Client-side validator so the Apply button is disabled when required
  // fields for the active dialog are missing. Prevents no-op API calls.
  const applyDisabled = useMemo(() => {
    if (bulkRunning) return true;
    if (!bulkKind) return true;
    if (bulkKind === "price" || bulkKind === "shipping") {
      const raw = bulkForm.value;
      if (raw === "" || raw === undefined || raw === null) return true;
      const n = parseFloat(raw);
      if (!isFinite(n)) return true;
    }
    if (bulkKind === "grading" && bulkForm.graded && !String(bulkForm.grade || "").trim()) return true;
    return false;
  }, [bulkKind, bulkForm, bulkRunning]);

  const runBulkDelete = async () => {
    setConfirmBulkDelete(false);
    setBulkRunning(true);
    try {
      const { data } = await api.post("/listings/bulk-delete", { ids: Array.from(selectedIds) });
      setListings((prev) => prev.filter((l) => !selectedIds.has(l.id)));
      clearAll();
      toast.success(`Deleted ${data.deleted} listing${data.deleted === 1 ? "" : "s"}`);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBulkRunning(false); }
  };

  const runBulkUpdate = async () => {
    const payload = { ids: Array.from(selectedIds) };
    if (bulkKind === "price") {
      const v = parseFloat(bulkForm.value);
      if (!isFinite(v)) { toast.error("Enter a numeric value"); return; }
      payload.price_op = { op: bulkForm.op, value: v };
    } else if (bulkKind === "shipping") {
      const v = parseFloat(bulkForm.value);
      if (!isFinite(v)) { toast.error("Enter a numeric value"); return; }
      payload.shipping_op = { op: bulkForm.op, value: v };
    } else if (bulkKind === "offers") payload.accepts_offers = !!bulkForm.accepts_offers;
    else if (bulkKind === "status") payload.status = bulkForm.status;
    else if (bulkKind === "condition") payload.condition = bulkForm.condition;
    else if (bulkKind === "grading") {
      payload.graded = !!bulkForm.graded;
      if (bulkForm.graded) {
        payload.grading_company = bulkForm.grading_company;
        payload.grade = bulkForm.grade;
      }
    } else if (bulkKind === "notes") payload.notes = bulkForm.notes || "";

    setBulkRunning(true);
    try {
      const { data } = await api.post("/listings/bulk-update", payload);
      toast.success(`Updated ${data.updated} listing${data.updated === 1 ? "" : "s"}`);
      closeBulk();
      // Refresh in the background so the UI reflects the changes.
      load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBulkRunning(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-3 md:px-8 py-4 md:py-8 pb-32">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-xs text-[#FF5F1F] tracking-widest uppercase">→ My Listings</div>
          <h1 className="mt-2 font-display font-black uppercase text-3xl md:text-4xl tracking-tighter">Your Cards</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/sell/bulk" data-testid="mylistings-bulk" className="h57-btn-secondary flex items-center gap-2 text-sm">
            <UploadSimple size={14} weight="bold" /> <span className="hidden sm:inline">Bulk Upload</span>
          </Link>
          <Link to="/sell" data-testid="mylistings-new" className="h57-btn-primary flex items-center gap-2">
            <Plus size={16} weight="bold" /> <span className="hidden sm:inline">New Listing</span>
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="py-24 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">Loading…</div>
      ) : listings.length === 0 ? (
        <div className="py-24 text-center">
          <div className="font-display font-black uppercase text-2xl text-zinc-500">No listings yet.</div>
          <p className="mt-2 text-sm text-zinc-600">Post your first card to start the hunt.</p>
          <Link to="/sell" className="h57-btn-primary inline-block mt-6">List a Card →</Link>
        </div>
      ) : (
        <>
          {/* Status tabs */}
          <div className="mb-4 flex gap-2 border-b border-white/10 pb-2 overflow-x-auto" data-testid="mylistings-tabs">
            {STATUS_TABS.map((t) => {
              const count = t.value ? (statusCounts[t.value] || 0) : statusCounts.all;
              const isActive = statusTab === t.value;
              return (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => changeTab(t.value)}
                  data-testid={`mylistings-tab-${t.value || "all"}`}
                  className={`h57-chip !py-2 !px-3 whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${
                    isActive ? `!border-[#FF5F1F] !text-[#FF5F1F]` : ""
                  }`}
                >
                  {t.label}
                  <span className={`font-mono text-[10px] ${isActive ? "opacity-100" : "opacity-60"}`}>({count})</span>
                </button>
              );
            })}
          </div>

          {visibleListings.length === 0 ? (
            <div className="py-16 text-center text-zinc-500 font-mono text-xs uppercase tracking-widest">
              No {statusTab === "hold" ? "on-hold" : statusTab || ""} listings.
            </div>
          ) : (
            <>
              {/* Select-all toolbar */}
              <div className="flex items-center gap-3 mb-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500" data-testid="mylistings-select-toolbar">
                <button
                  type="button"
                  onClick={allSelected ? clearAll : selectAll}
                  data-testid="mylistings-select-all"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  <span className={`w-3 h-3 border ${allSelected ? "bg-[#FF5F1F] border-[#FF5F1F]" : "border-white/30"}`} />
                  {allSelected ? "Deselect all" : `Select all (${visibleListings.length})`}
                </button>
                {someSelected && (
                  <span className="text-[#FF5F1F]" data-testid="mylistings-select-count">{selectedIds.size} selected</span>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
                {visibleListings.map((l) => {
              const isSelected = selectedIds.has(l.id);
              return (
                <div key={l.id} className={`relative group ${isSelected ? "ring-2 ring-[#FF5F1F]" : ""}`}>
                  <ListingCard listing={l} />
                  {/* Top-left: selection checkbox */}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleOne(l.id); }}
                    data-testid={`mylistings-select-${l.id}`}
                    title={isSelected ? "Deselect" : "Select"}
                    className={`absolute top-2 left-2 w-5 h-5 border-2 flex items-center justify-center transition-colors ${
                      isSelected ? "bg-[#FF5F1F] border-[#FF5F1F]" : "bg-[#0A0A0A]/80 border-white/30 hover:border-white"
                    }`}
                  >
                    {isSelected && <Check size={12} weight="bold" className="text-white" />}
                  </button>
                  {/* Top-right: status + edit */}
                  <div className="absolute top-2 right-2 flex flex-col gap-1 items-end pointer-events-none">
                    <span className={`h57-chip ${l.status === "active" ? "h57-chip-orange" : l.status === "sold" ? "h57-chip-gold" : ""}`}>
                      {l.status}
                    </span>
                    <Link to={`/listing/${l.id}/edit`} data-testid={`mylistings-edit-${l.id}`} onClick={(e) => e.stopPropagation()}
                      className="pointer-events-auto inline-flex items-center gap-1 bg-[#0A0A0A]/95 border border-white/20 hover:border-[#FF5F1F] hover:text-[#FF5F1F] text-white font-mono text-[10px] uppercase tracking-widest py-1 px-2 transition-colors" title="Edit listing">
                      <PencilSimple size={11} weight="bold" /> Edit
                    </Link>
                  </div>
                  {/* Bottom-right: delete */}
                  <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteListing(l); }}
                    disabled={deletingId === l.id} data-testid={`mylistings-delete-${l.id}`} title="Delete listing"
                    className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-[#0A0A0A]/95 border border-red-500/40 hover:border-red-500 hover:bg-red-500/10 text-red-400 hover:text-red-300 font-mono text-[10px] uppercase tracking-widest py-1 px-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <Trash size={11} weight="bold" />
                    {deletingId === l.id ? "…" : "Delete"}
                  </button>
                </div>
              );
            })}
              </div>
            </>
          )}
        </>
      )}

      {/* Bulk action bar */}
      {someSelected && (
        <div className="fixed left-0 right-0 bottom-16 md:bottom-0 z-30 border-t border-white/10 bg-[#0A0A0A]/95 backdrop-blur-xl px-3 md:px-8 py-3 flex items-center gap-3 flex-wrap" data-testid="mylistings-action-bar">
          <div className="font-mono text-xs uppercase tracking-widest text-[#FF5F1F]">{selectedIds.size} selected</div>
          <div className="flex flex-wrap items-center gap-2 flex-1">
            {BULK_ACTIONS.map((a) => (
              <button key={a.kind} type="button" onClick={() => openBulk(a.kind)} data-testid={`bulk-action-${a.kind}`}
                className="h57-btn-secondary text-xs inline-flex items-center gap-1">
                <a.icon size={12} weight="bold" /> {a.label}
              </button>
            ))}
            <button type="button" onClick={() => setConfirmBulkDelete(true)} disabled={bulkRunning} data-testid="bulk-action-delete"
              className="inline-flex items-center gap-1 border border-red-500/50 text-red-400 hover:bg-red-500/10 font-mono text-[10px] uppercase tracking-widest py-1.5 px-3 transition-colors">
              <Trash size={12} weight="bold" /> Delete {selectedIds.size}
            </button>
          </div>
          <button type="button" onClick={clearAll} data-testid="bulk-action-clear" className="text-zinc-500 hover:text-white p-1">
            <X size={16} weight="bold" />
          </button>
        </div>
      )}

      {/* Bulk edit dialog */}
      {bulkKind && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={closeBulk} data-testid="bulk-dialog">
          <div className="max-w-md w-full bg-[#0A0A0A] border border-white/15 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Bulk {bulkKind}</div>
                <div className="mt-1 font-display font-black uppercase text-lg">Apply to {selectedIds.size} listing{selectedIds.size === 1 ? "" : "s"}</div>
              </div>
              <button onClick={closeBulk} className="text-zinc-500 hover:text-white" data-testid="bulk-dialog-close"><X size={18} weight="bold" /></button>
            </div>

            <div className="space-y-3">
              {(bulkKind === "price" || bulkKind === "shipping") && (
                <>
                  <label className="h57-label">Operation</label>
                  <select className="h57-input" data-testid="bulk-op" value={bulkForm.op} onChange={(e) => setBulkForm({ ...bulkForm, op: e.target.value })}>
                    <option value="set">Set to (flat $)</option>
                    <option value="add">Add / subtract (+ or – $)</option>
                    <option value="multiply">Multiply by (e.g. 1.10 = +10%)</option>
                  </select>
                  <label className="h57-label">Value</label>
                  <input type="number" step="0.01" className="h57-input" data-testid="bulk-value"
                    placeholder={bulkForm.op === "multiply" ? "e.g. 1.10 for +10%" : "$"}
                    value={bulkForm.value} onChange={(e) => setBulkForm({ ...bulkForm, value: e.target.value })} />
                </>
              )}
              {bulkKind === "offers" && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!bulkForm.accepts_offers} data-testid="bulk-offers-checkbox"
                    onChange={(e) => setBulkForm({ ...bulkForm, accepts_offers: e.target.checked })} />
                  <span>Accept offers on the selected listings</span>
                </label>
              )}
              {bulkKind === "status" && (
                <>
                  <label className="h57-label">New status</label>
                  <select className="h57-input" data-testid="bulk-status" value={bulkForm.status} onChange={(e) => setBulkForm({ ...bulkForm, status: e.target.value })}>
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </>
              )}
              {bulkKind === "condition" && (
                <>
                  <label className="h57-label">Condition</label>
                  <select className="h57-input" data-testid="bulk-condition" value={bulkForm.condition} onChange={(e) => setBulkForm({ ...bulkForm, condition: e.target.value })}>
                    {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </>
              )}
              {bulkKind === "grading" && (
                <>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!bulkForm.graded} data-testid="bulk-graded"
                      onChange={(e) => setBulkForm({ ...bulkForm, graded: e.target.checked })} />
                    <span>Mark selected listings as graded</span>
                  </label>
                  {bulkForm.graded && (
                    <>
                      <label className="h57-label">Grading company</label>
                      <select className="h57-input" data-testid="bulk-grading-company" value={bulkForm.grading_company} onChange={(e) => setBulkForm({ ...bulkForm, grading_company: e.target.value })}>
                        {GRADING_COMPANIES.map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                      <label className="h57-label">Grade</label>
                      <input className="h57-input" data-testid="bulk-grade" value={bulkForm.grade || ""} onChange={(e) => setBulkForm({ ...bulkForm, grade: e.target.value })} placeholder="10" />
                    </>
                  )}
                </>
              )}
              {bulkKind === "notes" && (
                <>
                  <label className="h57-label">Notes (replaces existing on all selected)</label>
                  <textarea className="h57-input min-h-[80px]" data-testid="bulk-notes" value={bulkForm.notes || ""} onChange={(e) => setBulkForm({ ...bulkForm, notes: e.target.value })} />
                </>
              )}
            </div>

            <div className="mt-6 flex gap-2 justify-end">
              <button onClick={closeBulk} data-testid="bulk-dialog-cancel" className="h57-btn-secondary text-sm">Cancel</button>
              <button
                onClick={runBulkUpdate}
                disabled={applyDisabled}
                data-testid="bulk-dialog-apply"
                className="h57-btn-primary text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                title={applyDisabled && !bulkRunning ? "Fill in the required field first" : ""}
              >
                {bulkRunning ? "Applying…" : `Apply to ${selectedIds.size}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete confirmation — replaces the old window.confirm */}
      <AlertDialog open={confirmBulkDelete} onOpenChange={setConfirmBulkDelete}>
        <AlertDialogContent data-testid="bulk-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedIds.size} listing{selectedIds.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This removes the selected {selectedIds.size === 1 ? "listing" : "listings"} from the marketplace immediately.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runBulkDelete}
              data-testid="bulk-delete-confirm-btn"
              className="!bg-red-600 hover:!bg-red-500 border-red-600 text-white"
            >
              Delete {selectedIds.size}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
